export function getExtensionFromUrl(url: string): string | null {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() || '';
    const lastDot = lastSegment.lastIndexOf('.');
    if (lastDot === -1 || lastDot === lastSegment.length - 1) return null;
    return lastSegment.slice(lastDot + 1) || null;
  } catch {
    return null;
  }
}

export type DownloadLinkItem = { name?: string | null; url?: string | null };

export type DownloadProgressUpdate = {
  percent: number;
  completedCount: number;
  totalCount: number;
};

export type DownloadItemsFetchers = {
  getFileById: (id: number) => Promise<{ success?: boolean; data?: { name?: string; versions?: Array<{ name?: string | null; fileUrl?: string | null }> } }>;
  /** API'den dönen tip (data.items) ile uyumlu olması yeterli */
  getDownloadLinks: (path: string) => Promise<{ success?: boolean; data?: { items?: Array<{ name?: string | null; url?: string | null }> | null }; message?: string }>;
};

/**
 * Entry'ye göre indirilecek link listesini döndürür.
 * - File: Sadece getFileById kullanılır; versions içinde zaten url ve name var, getDownloadLinks çağrılmaz.
 * - Project/Folder: getDownloadLinks(path) ile items alınır.
 */
export async function getDownloadItemsForEntry(
  entry: { id?: number; path?: string; entryType?: string },
  fetchers: DownloadItemsFetchers
): Promise<DownloadLinkItem[]> {
  if (!entry) return [];
  if (entry.entryType === 'File' && entry.id != null) {
    const res = await fetchers.getFileById(entry.id);
    if (!res?.success || !res?.data?.versions?.length) return [];
    const fileName = res.data.name || 'file';
    return res.data.versions
      .filter((v: { fileUrl?: string | null }) => v?.fileUrl)
      .map((v: { name?: string | null; fileUrl?: string | null }, i: number) => ({
        name: v.name ? `${fileName}#${v.name}` : `${fileName}#v${i + 1}`,
        url: v.fileUrl ?? null,
      }));
  }
  if (entry.path) {
    const res = await fetchers.getDownloadLinks(entry.path);
    if (!res?.success || !('data' in res) || !res.data?.items?.length) return [];
    return res.data.items;
  }
  return [];
}

async function fetchBlobWithProgress(
  url: string,
  onFileProgress: (ratio: number) => void
): Promise<Blob | null> {
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) {
    console.error(`[Download] fetch failed:`, response.status, response.statusText);
    return null;
  }

  const totalBytes = Number(response.headers.get('content-length')) || 0;
  const reader = response.body?.getReader();

  if (!reader) {
    const blob = await response.blob();
    onFileProgress(1);
    return blob;
  }

  const chunks: Uint8Array[] = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onFileProgress(totalBytes > 0 ? Math.min(1, loaded / totalBytes) : 0.4);
  }

  onFileProgress(1);
  return new Blob(chunks as BlobPart[]);
}

/**
 * Tüm linkleri paralel fetch edip, indirmeleri kısa gecikmeyle tetikler (tarayıcı sıraya sokmasın diye).
 */
export async function triggerDownloadsFromItems(
  items: DownloadLinkItem[],
  options?: { onProgress?: (update: DownloadProgressUpdate) => void }
): Promise<void> {
  const validItems = items.filter((item): item is DownloadLinkItem & { url: string } => !!item?.url);
  const totalCount = validItems.length;
  if (totalCount === 0) return;

  const fileRatios = new Array<number>(totalCount).fill(0);

  const emitProgress = () => {
    const sum = fileRatios.reduce((acc, ratio) => acc + ratio, 0);
    options?.onProgress?.({
      percent: Math.min(100, Math.round((sum / totalCount) * 100)),
      completedCount: fileRatios.filter((ratio) => ratio >= 1).length,
      totalCount,
    });
  };

  emitProgress();

  const fetchPromises = validItems.map(async (item, index) => {
    const baseName = item.name || 'download';
    const ext = getExtensionFromUrl(item.url);
    const downloadFileName = ext ? `${baseName}.${ext}` : baseName;

    try {
      const blob = await fetchBlobWithProgress(item.url, (ratio) => {
        fileRatios[index] = ratio;
        emitProgress();
      });
      if (!blob) return null;
      return { downloadFileName, blob };
    } catch (error) {
      console.error(`[Download] ${downloadFileName} hata:`, error);
      return null;
    }
  });

  const settled = await Promise.allSettled(fetchPromises);
  const blobs: { downloadFileName: string; blob: Blob }[] = [];
  settled.forEach((result) => {
    if (result.status === 'fulfilled' && result.value) blobs.push(result.value);
    else if (result.status === 'rejected') console.error(`[Download] hata:`, result.reason);
  });

  fileRatios.fill(1);
  emitProgress();

  blobs.forEach(({ downloadFileName, blob }, index) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = downloadFileName;
    document.body.appendChild(link);
    setTimeout(() => {
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 2000);
    }, index * 80);
  });
}
